import React from 'react';
import { Box, Typography, IconButton, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ApartmentIcon from '@mui/icons-material/Apartment';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PlaceIcon from '@mui/icons-material/Place';
import LandscapeIcon from '@mui/icons-material/Landscape';
import {
  DemandSupplyResponse,
  DemandSupplyHdbBlock,
  DemandSupplyHdbResaleBlock,
  DemandSupplyCondoAddress,
  DemandSupplyNewLaunch,
  DemandSupplyGls,
  DemandSupplyOffByYearRow,
} from '../../services/api';
import { DEMAND_SUPPLY_COLORS } from './DemandSupplyPanel';

export type DemandSupplySelection =
  | { kind: 'hdb'; block: DemandSupplyHdbBlock }
  | { kind: 'hdbResale'; block: DemandSupplyHdbResaleBlock }
  | { kind: 'condo'; marker: DemandSupplyCondoAddress }
  | { kind: 'newLaunch'; marker: DemandSupplyNewLaunch }
  | { kind: 'gls'; site: DemandSupplyGls };

interface DemandSupplyDetailCardProps {
  selection: DemandSupplySelection;
  data: DemandSupplyResponse;
  onClose: () => void;
  /** Map Research glass console chrome — same footprint as ProjectPopupCard. */
  variant?: 'default' | 'mapResearch';
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatMonth = (month: string | null): string => {
  if (!month) return '—';
  const [y, m] = month.split('-');
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
};

const StatTile: React.FC<{ label: string; value: React.ReactNode; sub?: string; accent?: string }> = ({
  label,
  value,
  sub,
  accent,
}) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 130,
      border: '1px solid var(--color-border-strong)',
      borderRadius: 2,
      px: 1.5,
      py: 1.25,
    }}
  >
    <Typography
      variant="caption"
      sx={{ display: 'block', color: 'var(--color-text-muted)', letterSpacing: 0.5, textTransform: 'uppercase', fontSize: 10 }}
    >
      {label}
    </Typography>
    <Typography variant="h6" sx={{ fontWeight: 700, color: accent || 'var(--color-text-strong)', lineHeight: 1.3 }}>
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
        {sub}
      </Typography>
    )}
  </Box>
);

// Horizontal per-year bars with quarter chips, mirroring the reference design.
const OffByYearBars: React.FC<{ title: string; rows: DemandSupplyOffByYearRow[]; barColor: string; footnote?: string }> = ({
  title,
  rows,
  barColor,
  footnote,
}) => {
  if (!rows.length) return null;
  const max = Math.max(...rows.map((r) => r.total));
  return (
    <Box sx={{ border: '1px solid var(--color-border-strong)', borderRadius: 2, px: 1.5, py: 1.25, mt: 1.5 }}>
      <Typography
        variant="caption"
        sx={{ display: 'block', mb: 1, color: 'var(--color-text-muted)', letterSpacing: 0.5, textTransform: 'uppercase', fontSize: 10 }}
      >
        {title}
      </Typography>
      {rows.map((row) => {
        const quarters = ([1, 2, 3, 4] as const)
          .map((q) => ({ q, units: row[`q${q}` as 'q1' | 'q2' | 'q3' | 'q4'] }))
          .filter((x) => x.units > 0);
        return (
          <Box key={row.year} sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'var(--color-text-strong)', width: 42, flexShrink: 0 }}>
                {row.year}
              </Typography>
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: barColor,
                    width: `${Math.max((row.total / max) * 100, 4)}%`,
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'var(--color-text-strong)', minWidth: 44, textAlign: 'right' }}>
                {row.total.toLocaleString()}
              </Typography>
            </Box>
            {quarters.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', pl: '50px', mt: 0.25 }}>
                {quarters.map(({ q, units }) => (
                  <Typography key={q} variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                    Q{q} · {units.toLocaleString()}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        );
      })}
      {footnote && (
        <Typography variant="caption" sx={{ display: 'block', color: 'var(--color-text-subtle)' }}>
          {footnote}
        </Typography>
      )}
    </Box>
  );
};

const InfoStrip: React.FC<{ text: string }> = ({ text }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      mt: 1.5,
      px: 1.5,
      py: 1,
      borderRadius: 2,
      bgcolor: 'var(--color-surface-ghost)',
    }}
  >
    <PlaceIcon sx={{ fontSize: 16, color: 'var(--color-text-muted)' }} />
    <Typography variant="caption" sx={{ color: 'var(--color-text)' }}>
      {text}
    </Typography>
  </Box>
);

const DemandSupplyDetailCard: React.FC<DemandSupplyDetailCardProps> = ({
  selection,
  data,
  onClose,
  variant = 'default',
}) => {
  const mapResearch = variant === 'mapResearch';
  const currentYear = parseInt(data.asOf.slice(0, 4), 10);
  const colors = DEMAND_SUPPLY_COLORS.light;

  let badgeIcon: React.ReactElement = <ApartmentIcon sx={{ fontSize: 14 }} />;
  let badgeLabel = '';
  let title = '';
  let distance = 0;
  let body: React.ReactNode = null;

  const ssdFootnote =
    "Units entering the resale market as their Seller's Stamp Duty window expires (3-year, or 4-year for purchases " +
    'on/after 4 Jul 2025; EC new sales ≈ 8 years). Estimate — not a guarantee of individual unit eligibility.';

  if (selection.kind === 'condo' || selection.kind === 'newLaunch') {
    const marker = selection.kind === 'condo' ? selection.marker : selection.marker;
    const spProjectId = marker.spProjectId;
    const card = spProjectId != null ? data.demand.condoProjects?.[String(spProjectId)] : undefined;
    badgeIcon = selection.kind === 'newLaunch' ? <StorefrontIcon sx={{ fontSize: 14 }} /> : <ApartmentIcon sx={{ fontSize: 14 }} />;
    badgeLabel = selection.kind === 'newLaunch' ? 'New Launch' : 'Condo off SSD';
    title = card?.name || marker.name;
    distance = marker.distance;

    if (card) {
      const total = card.totalUnits ?? card.trackedUnits;
      const underConstruction = card.topYear != null && card.topYear >= currentYear;
      const firstLockedYear = card.firstLockedExpiryMonth ? card.firstLockedExpiryMonth.slice(0, 4) : null;
      body = (
        <>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <StatTile
              label="Still SSD-locked"
              value={card.lockedUnits.toLocaleString()}
              sub={`of ${total.toLocaleString()} total`}
              accent={colors.condo}
            />
            <StatTile
              label="First SSD expiry (locked)"
              value={firstLockedYear ?? '—'}
              sub={card.firstLockedExpiryMonth ? formatMonth(card.firstLockedExpiryMonth) : undefined}
            />
            {card.availableUnits != null && (
              <StatTile
                label="New launch available"
                value={card.availableUnits.toLocaleString()}
                sub={card.totalUnits ? `of ${card.totalUnits.toLocaleString()} total` : 'developer inventory'}
                accent={colors.newLaunch}
              />
            )}
          </Box>
          {underConstruction ? (
            <Typography variant="body2" sx={{ mt: 1.5, color: 'var(--color-text)' }}>
              This project hasn't been completed (TOP {card.topYear}) yet — the{' '}
              <strong>{card.lockedUnits.toLocaleString()}</strong> of <strong>{total.toLocaleString()}</strong> units above were
              bought at launch and stay SSD-locked until the year shown; unsold units here are new-launch inventory from the
              developer, not owners free to sell.
            </Typography>
          ) : (
            card.offSsdUnits > 0 && (
              <Typography variant="body2" sx={{ mt: 1.5, color: 'var(--color-text)' }}>
                {card.offSsdUnits.toLocaleString()} tracked units are already past their SSD window and free to resell.
              </Typography>
            )
          )}
          <OffByYearBars
            title="Units off SSD by year"
            rows={card.offByYear}
            barColor={colors.condo}
            footnote="Q1–Q4 are SSD-expiry quarters computed from actual purchase dates."
          />
          <InfoStrip text="SSD expiry — units can resell." />
        </>
      );
    } else if (selection.kind === 'newLaunch') {
      const nl = selection.marker;
      body = (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <StatTile
            label="Available units"
            value={nl.availableUnits.toLocaleString()}
            sub={nl.totalUnits ? `of ${nl.totalUnits.toLocaleString()} total` : undefined}
            accent={colors.newLaunch}
          />
          {nl.topYear != null && <StatTile label="Expected TOP" value={nl.topYear} />}
        </Box>
      );
    } else {
      const c = selection.marker as DemandSupplyCondoAddress;
      body = (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <StatTile label="Units reaching SSD expiry" value={c.units.toLocaleString()} accent={colors.condo} />
          <StatTile label="Expiry window" value={`${formatMonth(c.firstExpiryMonth)} – ${formatMonth(c.lastExpiryMonth)}`} />
        </Box>
      );
    }
  } else if (selection.kind === 'hdb') {
    const block = selection.block;
    badgeIcon = <HomeWorkIcon sx={{ fontSize: 14 }} />;
    badgeLabel = 'HDB MOP';
    title = block.address;
    distance = block.distance;
    const locked = block.mopYear >= currentYear;
    body = (
      <>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <StatTile
            label={locked ? 'Still MOP-locked' : 'Off MOP'}
            value={block.units.toLocaleString()}
            sub={`of ${block.units.toLocaleString()} block units`}
            accent={colors.hdbBto}
          />
          <StatTile label="MOP year" value={block.mopYear} sub={`completed ${block.completedYear}`} />
        </Box>
        <Typography variant="body2" sx={{ mt: 1.5, color: 'var(--color-text)' }}>
          Completed in <strong>{block.completedYear}</strong> — owners can first resell 5 years later, so the whole block of{' '}
          <strong>{block.units.toLocaleString()}</strong> units becomes resale-eligible during <strong>{block.mopYear}</strong>.
        </Typography>
        <OffByYearBars
          title="Units off MOP by year"
          rows={[{ year: block.mopYear, q1: 0, q2: 0, q3: 0, q4: 0, total: block.units }]}
          barColor={colors.hdbBto}
          footnote="Exact months are unknown — HDB records completion year only, so the whole block lands in its MOP year."
        />
        <InfoStrip text="MOP reached — flats can be resold on the open market." />
      </>
    );
  } else if (selection.kind === 'hdbResale') {
    const block = selection.block;
    badgeIcon = <HomeWorkIcon sx={{ fontSize: 14 }} />;
    badgeLabel = 'HDB Resale MOP';
    title = block.address;
    distance = block.distance;
    const firstLockedYear = block.firstLockedMopMonth ? block.firstLockedMopMonth.slice(0, 4) : null;
    body = (
      <>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <StatTile
            label="Still MOP-locked"
            value={block.lockedUnits.toLocaleString()}
            sub={block.blockUnits ? `of ${block.blockUnits.toLocaleString()} block units` : 'resale purchases tracked'}
            accent={colors.hdbResale}
          />
          <StatTile
            label="First MOP (locked)"
            value={firstLockedYear ?? '—'}
            sub={block.firstLockedMopMonth ? formatMonth(block.firstLockedMopMonth) : undefined}
          />
        </Box>
        {block.offMopUnits > 0 && (
          <Typography variant="body2" sx={{ mt: 1.5, color: 'var(--color-text)' }}>
            {block.offMopUnits.toLocaleString()} tracked resale purchases in this block are already past their MOP.
          </Typography>
        )}
        <OffByYearBars
          title="Units off MOP by year"
          rows={block.offByYear}
          barColor={colors.hdbResale}
          footnote="Q1–Q4 are MOP quarters computed from actual resale purchase dates (purchase + 5 years)."
        />
        <InfoStrip text="MOP reached — flats can be resold on the open market." />
      </>
    );
  } else {
    // GLS site
    const site = selection.site;
    badgeIcon = <LandscapeIcon sx={{ fontSize: 14 }} />;
    badgeLabel = 'GLS';
    title = site.siteName || site.location || 'GLS site';
    distance = site.distance;
    body = (
      <>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <StatTile
            label="Housing units"
            value={site.units != null ? site.units.toLocaleString() : '—'}
            accent={colors.gls}
          />
          <StatTile
            label="Status"
            value={site.awarded ? 'Awarded' : 'Not awarded'}
            sub={site.siteStatus || undefined}
          />
          {site.landUse && <StatTile label="Land use" value={site.landUse} />}
          {site.expectedTop && <StatTile label="Expected TOP" value={site.expectedTop} />}
        </Box>
        {site.awarded ? (
          <Typography variant="body2" sx={{ mt: 1.5, color: 'var(--color-text)' }}>
            Awarded{site.dateOfAward ? ` on ${new Date(site.dateOfAward).toLocaleDateString()}` : ''}
            {site.supplyMonth ? (
              <>
                {' '}
                — units enter the competitive pool around <strong>{formatMonth(site.supplyMonth)}</strong> (12 months after
                award).
              </>
            ) : (
              '.'
            )}
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ mt: 1.5, color: 'var(--color-text)' }}>
            This GLS site has not been awarded yet, so units are not timed into the supply pool.
          </Typography>
        )}
        {site.tenderer && (
          <Typography variant="body2" sx={{ mt: 1, color: 'var(--color-text-muted)' }}>
            Successful tenderer: <strong style={{ color: 'var(--color-text)' }}>{site.tenderer}</strong>
          </Typography>
        )}
        {site.location && site.siteName && (
          <InfoStrip text={site.location} />
        )}
      </>
    );
  }

  const footnote =
    selection.kind === 'hdb' || selection.kind === 'hdbResale'
      ? 'Flats entering the resale market as their 5-year Minimum Occupation Period ends. Estimate — Prime/Plus flats carry a 10-year MOP.'
      : selection.kind === 'gls'
        ? 'Government Land Sales sites not yet linked to a named project. Awarded parcels count as competitive supply 12 months after the award date.'
        : ssdFootnote;

  return (
    <Box
      className={mapResearch ? 'mr-popup-card' : undefined}
      sx={
        mapResearch
          ? {
              position: 'relative',
              width: '100%',
              maxWidth: 396,
              maxHeight: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: '16px',
              overflow: 'hidden',
              color: '#eef6fa',
              background:
                'linear-gradient(160deg, rgba(10, 18, 32, 0.94) 0%, rgba(13, 27, 42, 0.94) 55%, rgba(9, 22, 34, 0.96) 100%)',
              backdropFilter: 'blur(14px)',
              border: '1px solid rgba(34, 211, 238, 0.3)',
              boxShadow:
                '0 24px 64px rgba(2, 8, 20, 0.6), 0 0 32px rgba(34, 211, 238, 0.12), inset 0 1px 0 rgba(255,255,255,0.06)',
              // Remap theme tokens used by StatTile / OffByYearBars / InfoStrip.
              '--color-text': '#eef6fa',
              '--color-text-strong': '#f1f7fb',
              '--color-text-muted': 'rgba(148, 197, 222, 0.75)',
              '--color-text-subtle': 'rgba(196, 220, 233, 0.55)',
              '--color-border-strong': 'rgba(34, 211, 238, 0.22)',
              '--color-surface-ghost': 'rgba(34, 211, 238, 0.08)',
              '--color-info-rgb': '34, 211, 238',
            }
          : {
              position: 'absolute',
              inset: 12,
              zIndex: 1200,
              bgcolor: 'var(--color-surface)',
              border: '1px solid var(--color-border-strong)',
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
              p: 2,
              overflowY: 'auto',
            }
      }
    >
      {mapResearch && (
        <>
          <Box className="mr-scanline" />
          {(['tl', 'tr', 'bl', 'br'] as const).map((corner) => (
            <Box
              key={corner}
              sx={{
                position: 'absolute',
                width: 14,
                height: 14,
                zIndex: 3,
                pointerEvents: 'none',
                borderColor: 'rgba(34, 211, 238, 0.75)',
                borderStyle: 'solid',
                borderWidth: 0,
                ...(corner === 'tl' && { top: 6, left: 6, borderTopWidth: 2, borderLeftWidth: 2 }),
                ...(corner === 'tr' && { top: 6, right: 6, borderTopWidth: 2, borderRightWidth: 2 }),
                ...(corner === 'bl' && { bottom: 6, left: 6, borderBottomWidth: 2, borderLeftWidth: 2 }),
                ...(corner === 'br' && { bottom: 6, right: 6, borderBottomWidth: 2, borderRightWidth: 2 }),
              }}
            />
          ))}
        </>
      )}

      <Box sx={mapResearch ? { overflowY: 'auto', p: 2, pt: 1.75 } : undefined}>
        {mapResearch && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
            <Box className="mr-pulse-dot" />
            <Typography
              sx={{
                fontFamily: '"SF Mono", "Roboto Mono", Menlo, monospace',
                fontSize: 9.5,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: '#22d3ee',
                flex: 1,
              }}
            >
              Super Ratio · Super Map
            </Typography>
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ color: 'rgba(238, 246, 250, 0.7)', mr: -0.5, mt: -0.5 }}
              aria-label="Close details"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Chip
            icon={badgeIcon}
            label={badgeLabel}
            size="small"
            sx={
              mapResearch
                ? {
                    height: 22,
                    fontSize: 10.5,
                    fontWeight: 700,
                    bgcolor: 'rgba(34, 211, 238, 0.12)',
                    color: '#a5f3fc',
                    border: '1px solid rgba(34, 211, 238, 0.3)',
                    '& .MuiChip-icon': { color: '#a5f3fc' },
                  }
                : {
                    bgcolor: 'rgba(var(--color-info-rgb), 0.12)',
                    color: 'var(--color-text-strong)',
                    fontWeight: 600,
                    '& .MuiChip-icon': { color: 'var(--color-text-strong)' },
                  }
            }
          />
          {!mapResearch && (
            <IconButton size="small" onClick={onClose} sx={{ color: 'var(--color-text-muted)' }} aria-label="Close details">
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 800,
            mt: 0.5,
            ...(mapResearch
              ? {
                  fontSize: 21,
                  lineHeight: 1.15,
                  background: 'linear-gradient(90deg, #ffffff 0%, #a5f3fc 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }
              : { color: 'var(--color-text-strong)' }),
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mb: 1.5,
            color: mapResearch ? 'rgba(196, 220, 233, 0.65)' : 'var(--color-text-muted)',
          }}
        >
          {Math.round(distance)}m from focus
        </Typography>
        {body}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 1.5,
            color: mapResearch ? 'rgba(196, 220, 233, 0.55)' : 'var(--color-text-subtle)',
            lineHeight: 1.45,
          }}
        >
          {footnote}
        </Typography>
      </Box>
    </Box>
  );
};

export default DemandSupplyDetailCard;
